/**
 * Retro UI Chrome Components - Design System
 * 
 * Comprehensive design tokens based on reference images (JJQs0.jpg, image.jpg):
 * - Framed panels with visible corner rivets/bolts
 * - Gradient-shaded pixel logo type
 * - Chunky beveled buttons (grass primary, cream secondary, dirt tertiary)
 * - Textured backgrounds (dotted, diagonal stripe)
 * - LED scoreboard treatment
 * - Consistent with locked palette
 */

import React from 'react';

// Base palette (locked - do not change)
export const PALETTE = {
  cream: '#F5F5DC',
  grass: '#4B8B3B',
  dirt: '#8B4513',
  ink: '#1a1a1a',
  scoreboardBg: '#000000',
  scoreboardText: '#00FF00',
};

// Pixel-art pattern builders (crisp SVG tiles - no smooth CSS gradients)
export const pixelStripes = (base, dark) => {
  const cells = [[0,0],[0,8],[8,0],[8,24],[16,16],[16,24],[24,8],[24,16]];
  const rects = cells.map(([x,y]) => `%3Crect x='${x}' y='${y}' width='8' height='8'/%3E`).join('');
  return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' shape-rendering='crispEdges'%3E%3Crect width='32' height='32' fill='%23${base}'/%3E%3Cg fill='%23${dark}'%3E${rects}%3C/g%3E%3C/svg%3E")`;
};

const pixelDots = (color, opacity) => {
  return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' shape-rendering='crispEdges'%3E%3Crect x='4' y='4' width='3' height='3' fill='%23${color}' fill-opacity='${opacity}'/%3E%3C/svg%3E")`;
};

// Hard-stop banded gradient for 16-bit button shading (no smooth blends)
export const bandedGradient = (light, base, dark) =>
  `linear-gradient(180deg, ${light} 0%, ${light} 30%, ${base} 30%, ${base} 72%, ${dark} 72%, ${dark} 100%)`;

// Accent colors from reference set (selection gold, action red)
export const ACCENTS = {
  gold: '#F2C438',
  goldLight: '#FFDE6B',
  goldDark: '#C99B1F',
  red: '#C0392B',
};

// Design tokens for reusability
export const DESIGN_TOKENS = {
  // Border radiuses (SNES-era rounded frames per reference set)
  borderRadius: {
    none: '0px',
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    pill: '999px',
  },
  // Shadow styles for depth
  shadows: {
    button: `4px 4px 0px ${PALETTE.ink}`,
    buttonPressed: `2px 2px 0px ${PALETTE.ink}`,
    panel: `6px 6px 0px ${PALETTE.ink}`,
    inset: `inset 2px 2px 4px rgba(0,0,0,0.3)`,
  },
  // Border widths
  borders: {
    thin: '2px',
    medium: '3px',
    thick: '4px',
  },
  // TWO-TIER FONT SYSTEM (8-bit → 16-bit upgrade)
  // display: Bold, heavy - for logos, hero results (HR/HOME RUN/GAME OVER), major headers
  // ui: Rounder, mixed-case, legible - for buttons, tabs, labels, body text, inputs
  fonts: {
    display: "'Press Start 2P', cursive",  // Keep for big display moments
    ui: "'Pixelify Sans', sans-serif",     // Rounder 16-bit SNES-style for UI
    mono: "'VT323', monospace",            // Monospace for LED displays
  },
  // SIZE HIERARCHY (clear big-title / medium-header / small-body steps)
  fontSizes: {
    // Display sizes - large and bold
    displayXl: '32px',  // Logo, GAME OVER
    displayLg: '24px',  // HOME RUN!, major results
    displayMd: '18px',  // Section headers
    displaySm: '14px',  // Minor headers
    // UI sizes - readable body text
    uiXl: '20px',       // Large buttons, prominent labels
    uiLg: '17px',       // Standard buttons
    uiMd: '15px',       // Body text, labels
    uiSm: '13px',       // Secondary text
    uiXs: '11px',       // Hints, captions
  },
  // Texture patterns (CSS)
  textures: {
    dots: `radial-gradient(circle at 2px 2px, rgba(0,0,0,0.08) 1px, transparent 0)`,
    dotsLight: `radial-gradient(circle at 2px 2px, rgba(75,139,59,0.1) 1px, transparent 0)`,
    dotsDark: `radial-gradient(circle at 2px 2px, rgba(139,69,19,0.12) 1px, transparent 0)`,
    diagonal: `repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(0,0,0,0.05) 4px, rgba(0,0,0,0.05) 8px)`,
  },
};

/**
 * RetroButton - Chunky beveled pixel art button
 * 
 * Variants:
 * - primary: Green (grass) background - main actions
 * - secondary: Cream background - secondary actions
 * - tertiary: Dirt brown - admin/alternate actions
 * - danger: Red background - destructive actions
 * - ghost: Transparent with border
 */
export function RetroButton({ 
  children, 
  variant = 'primary', 
  size = 'md',
  disabled = false,
  fullWidth = false,
  className = '',
  onClick,
  ...props 
}) {
  const baseStyles = {
    fontFamily: DESIGN_TOKENS.fonts.ui,
    fontWeight: 600,
    imageRendering: 'pixelated',
    transition: 'all 0.1s ease',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    display: fullWidth ? 'block' : 'inline-block',
    width: fullWidth ? '100%' : 'auto',
    borderRadius: DESIGN_TOKENS.borderRadius.lg,
  };

  const sizeStyles = {
    xs: { fontSize: DESIGN_TOKENS.fontSizes.uiXs, padding: '6px 12px' },
    sm: { fontSize: DESIGN_TOKENS.fontSizes.uiSm, padding: '8px 16px' },
    md: { fontSize: DESIGN_TOKENS.fontSizes.uiMd, padding: '12px 24px' },
    lg: { fontSize: DESIGN_TOKENS.fontSizes.uiLg, padding: '16px 32px' },
  };

  const variantStyles = {
    primary: {
      background: bandedGradient(adjustColor(PALETTE.grass, 15), PALETTE.grass, adjustColor(PALETTE.grass, -20)),
      color: PALETTE.cream,
      border: `${DESIGN_TOKENS.borders.medium} solid ${PALETTE.ink}`,
      boxShadow: `
        inset 2px 2px 0px ${adjustColor(PALETTE.grass, 40)},
        inset -2px -2px 0px ${adjustColor(PALETTE.grass, -40)},
        ${DESIGN_TOKENS.shadows.button}
      `,
    },
    secondary: {
      background: bandedGradient('#FFFFFF', PALETTE.cream, adjustColor(PALETTE.cream, -20)),
      color: PALETTE.ink,
      border: `${DESIGN_TOKENS.borders.medium} solid ${PALETTE.ink}`,
      boxShadow: `
        inset 2px 2px 0px #FFFFFF,
        inset -2px -2px 0px ${adjustColor(PALETTE.cream, -35)},
        ${DESIGN_TOKENS.shadows.button}
      `,
    },
    tertiary: {
      background: bandedGradient(adjustColor(PALETTE.dirt, 20), PALETTE.dirt, adjustColor(PALETTE.dirt, -25)),
      color: PALETTE.cream,
      border: `${DESIGN_TOKENS.borders.medium} solid ${PALETTE.ink}`,
      boxShadow: `
        inset 2px 2px 0px ${adjustColor(PALETTE.dirt, 40)},
        inset -2px -2px 0px ${adjustColor(PALETTE.dirt, -40)},
        ${DESIGN_TOKENS.shadows.button}
      `,
    },
    danger: {
      background: bandedGradient('#E53E3E', '#C53030', '#9B2C2C'),
      color: PALETTE.cream,
      border: `${DESIGN_TOKENS.borders.medium} solid ${PALETTE.ink}`,
      boxShadow: `
        inset 2px 2px 0px #FC8181,
        inset -2px -2px 0px #742A2A,
        ${DESIGN_TOKENS.shadows.button}
      `,
    },
    ghost: {
      background: 'transparent',
      color: PALETTE.cream,
      border: `2px solid ${PALETTE.cream}`,
      boxShadow: 'none',
    },
    ghostDark: {
      background: 'transparent',
      color: PALETTE.ink,
      border: `2px solid ${PALETTE.ink}`,
      boxShadow: 'none',
    },
  };

  const hoverClass = disabled ? '' : 'hover:translate-y-0.5 active:translate-y-1';

  return (
    <button
      className={`text-center ${hoverClass} ${className}`}
      style={{
        ...baseStyles,
        ...sizeStyles[size],
        ...variantStyles[variant],
      }}
      disabled={disabled}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
}

/**
 * RetroPanel - Framed panel container with corner rivets
 */
export function RetroPanel({ 
  children, 
  title,
  variant = 'default',
  showRivets = true,
  className = '',
  ...props 
}) {
  const variantStyles = {
    // Light cream panel with green border - primary panels
    default: {
      background: PALETTE.cream,
      borderColor: PALETTE.grass,
      titleColor: PALETTE.grass,
      titleBg: 'transparent',
    },
    // Light cream panel with ink border
    light: {
      background: PALETTE.cream,
      borderColor: PALETTE.ink,
      titleColor: PALETTE.ink,
      titleBg: 'transparent',
    },
    // Light cream panel with dirt/brown border - secondary panels
    secondary: {
      background: PALETTE.cream,
      borderColor: PALETTE.dirt,
      titleColor: PALETTE.dirt,
      titleBg: 'transparent',
    },
    // Dark panel for LED/scoreboard displays only
    scoreboard: {
      background: PALETTE.scoreboardBg,
      borderColor: PALETTE.grass,
      titleColor: PALETTE.scoreboardText,
      titleBg: 'transparent',
      isLED: true,
    },
  };

  const style = variantStyles[variant];

  // Rivet component
  const Rivet = ({ position }) => {
    const positions = {
      topLeft: { top: '6px', left: '6px' },
      topRight: { top: '6px', right: '6px' },
      bottomLeft: { bottom: '6px', left: '6px' },
      bottomRight: { bottom: '6px', right: '6px' },
    };
    
    return (
      <div 
        className="absolute w-3 h-3 rounded-full"
        style={{ 
          ...positions[position],
          background: `radial-gradient(circle at 30% 30%, ${adjustColor(style.borderColor, 40)}, ${style.borderColor}, ${adjustColor(style.borderColor, -30)})`,
          boxShadow: `inset 1px 1px 0px rgba(255,255,255,0.4), inset -1px -1px 0px rgba(0,0,0,0.4)`,
          border: `1px solid ${adjustColor(style.borderColor, -40)}`,
        }} 
      />
    );
  };

  return (
    <div 
      className={`relative ${className}`}
      style={{
        background: style.background,
        border: `${DESIGN_TOKENS.borders.thick} solid ${style.borderColor}`,
        boxShadow: DESIGN_TOKENS.shadows.panel,
        borderRadius: DESIGN_TOKENS.borderRadius.xl,
      }}
      {...props}
    >
      {/* Corner rivets */}
      {showRivets && (
        <>
          <Rivet position="topLeft" />
          <Rivet position="topRight" />
          <Rivet position="bottomLeft" />
          <Rivet position="bottomRight" />
        </>
      )}
      
      {/* Inner border highlight */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          border: `2px solid ${adjustColor(style.borderColor, 30)}33`,
          margin: '2px',
          borderRadius: DESIGN_TOKENS.borderRadius.lg,
        }}
      />
      
      {title && (
        <div 
          className="text-center py-3 mx-6"
          style={{
            fontFamily: DESIGN_TOKENS.fonts.display,
            fontSize: DESIGN_TOKENS.fontSizes.displaySm,
            color: style.titleColor,
            borderBottom: `2px solid ${style.borderColor}`,
            textShadow: style.isLED 
              ? `0 0 10px ${PALETTE.scoreboardText}` 
              : `2px 2px 0px ${adjustColor(style.titleColor, -40)}`,
            letterSpacing: '2px',
          }}
        >
          {title}
        </div>
      )}
      
      <div className="p-5">
        {children}
      </div>
    </div>
  );
}

/**
 * RetroTabs - Tab navigation component
 */
export function RetroTabs({ 
  tabs, 
  activeTab, 
  onTabChange,
  className = '',
}) {
  return (
    <div 
      className={`flex ${className}`}
      style={{
        background: PALETTE.ink,
        border: `2px solid ${PALETTE.grass}`,
        borderBottom: 'none',
      }}
    >
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          className="flex-1 text-center py-2 px-3 transition-all"
          style={{
            fontFamily: "'Press Start 2P', cursive",
            fontSize: '8px',
            color: activeTab === tab.id ? PALETTE.ink : PALETTE.cream,
            background: activeTab === tab.id 
              ? `linear-gradient(180deg, ${PALETTE.cream} 0%, ${adjustColor(PALETTE.cream, -20)} 100%)`
              : 'transparent',
            borderRight: `2px solid ${PALETTE.grass}`,
          }}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

/**
 * RetroInput - Styled text input
 */
export function RetroInput({
  type = 'text',
  placeholder,
  value,
  onChange,
  className = '',
  ...props
}) {
  return (
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className={`w-full ${className}`}
      style={{
        fontFamily: "'Press Start 2P', cursive",
        fontSize: '10px',
        padding: '10px 12px',
        background: PALETTE.ink,
        color: PALETTE.cream,
        border: `3px solid ${PALETTE.grass}`,
        boxShadow: `inset 2px 2px 0px ${PALETTE.scoreboardBg}`,
        outline: 'none',
      }}
      {...props}
    />
  );
}

/**
 * RetroSelect - Styled select dropdown
 */
export function RetroSelect({
  value,
  onChange,
  options,
  className = '',
  ...props
}) {
  return (
    <select
      value={value}
      onChange={onChange}
      className={`w-full ${className}`}
      style={{
        fontFamily: "'Press Start 2P', cursive",
        fontSize: '10px',
        padding: '10px 12px',
        background: PALETTE.ink,
        color: PALETTE.cream,
        border: `3px solid ${PALETTE.grass}`,
        boxShadow: `inset 2px 2px 0px ${PALETTE.scoreboardBg}`,
        outline: 'none',
        cursor: 'pointer',
      }}
      {...props}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

/**
 * RetroToggle - Toggle switch styled as tab selector
 */
export function RetroToggle({
  options,
  value,
  onChange,
  className = '',
}) {
  return (
    <div 
      className={`inline-flex ${className}`}
      style={{
        background: PALETTE.ink,
        border: `2px solid ${PALETTE.grass}`,
      }}
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className="px-4 py-2 transition-all"
          style={{
            fontFamily: "'Press Start 2P', cursive",
            fontSize: '10px',
            color: value === opt.value ? PALETTE.ink : PALETTE.cream,
            background: value === opt.value 
              ? PALETTE.grass
              : 'transparent',
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/**
 * RetroHeading - Styled heading with optional glow
 */
export function RetroHeading({
  children,
  level = 1,
  glow = false,
  className = '',
}) {
  const sizes = {
    1: '24px',
    2: '18px',
    3: '14px',
    4: '12px',
  };

  const Tag = `h${level}`;

  return (
    <Tag
      className={className}
      style={{
        fontFamily: "'Press Start 2P', cursive",
        fontSize: sizes[level],
        color: glow ? PALETTE.scoreboardText : PALETTE.cream,
        textShadow: glow ? `0 0 10px ${PALETTE.scoreboardText}, 0 0 20px ${PALETTE.scoreboardText}` : 'none',
      }}
    >
      {children}
    </Tag>
  );
}

/**
 * ScoreboardDisplay - LED-style scoreboard text
 */
export function ScoreboardDisplay({
  children,
  size = 'md',
  className = '',
}) {
  const sizes = {
    sm: '12px',
    md: '18px',
    lg: '24px',
    xl: '32px',
  };

  return (
    <div
      className={className}
      style={{
        fontFamily: "'Press Start 2P', cursive",
        fontSize: sizes[size],
        color: PALETTE.scoreboardText,
        textShadow: `
          0 0 5px ${PALETTE.scoreboardText},
          0 0 10px ${PALETTE.scoreboardText},
          0 0 20px ${PALETTE.scoreboardText}
        `,
        background: PALETTE.scoreboardBg,
        padding: '8px 16px',
        border: `2px solid ${PALETTE.grass}`,
      }}
    >
      {children}
    </div>
  );
}

/**
 * RetroBackground - Textured background wrapper with multiple variants
 */
export function RetroBackground({
  children,
  variant = 'field',
  className = '',
}) {
  const backgrounds = {
    // Light cream with green pixel-dot texture - for dashboard, menus
    field: {
      backgroundColor: PALETTE.cream,
      backgroundImage: pixelDots('4B8B3B', 0.18),
      backgroundSize: '20px 20px',
    },
    // Light tan/brown with subtle pixel-dot texture - for game setup
    dirt: {
      backgroundColor: '#E8DCC8',
      backgroundImage: pixelDots('8B4513', 0.14),
      backgroundSize: '20px 20px',
    },
    // Light cream with subtle pixel dots - for auth screens
    cream: {
      backgroundColor: PALETTE.cream,
      backgroundImage: pixelDots('1a1a1a', 0.1),
      backgroundSize: '20px 20px',
    },
    // Grass green with pixel-dot texture - for gameplay (correct per reference)
    grass: {
      backgroundColor: PALETTE.grass,
      backgroundImage: pixelDots('000000', 0.14),
      backgroundSize: '20px 20px',
    },
    // Solid cream
    solidCream: {
      backgroundColor: PALETTE.cream,
    },
    // Grey-tan for team entry screens
    greyTan: {
      backgroundColor: '#D4CFC4',
      backgroundImage: pixelDots('000000', 0.08),
      backgroundSize: '20px 20px',
    },
    // Dark brown pixel-stepped diagonal stripes - GAME SETUP per reference
    brownStripes: {
      backgroundColor: '#6B4226',
      backgroundImage: pixelStripes('6B4226', '583619'),
      backgroundSize: '64px 64px',
    },
    // Lighter tan pixel-stepped diagonal stripes - team/player entry per reference
    tanStripes: {
      backgroundColor: '#C6BBA5',
      backgroundImage: pixelStripes('C6BBA5', 'B6AA92'),
      backgroundSize: '64px 64px',
    },
    // Dark green pixel-stepped diagonal stripes - inning summary / game over per reference
    greenStripes: {
      backgroundColor: '#2F5D25',
      backgroundImage: pixelStripes('2F5D25', '264C1D'),
      backgroundSize: '64px 64px',
    },
  };

  return (
    <div 
      className={`min-h-screen ${className}`}
      style={backgrounds[variant]}
    >
      {children}
    </div>
  );
}

/**
 * RetroCard - Light panel for auth forms, cards
 */
export function RetroCard({
  children,
  title,
  showRivets = true,
  className = '',
}) {
  // Rivet component
  const Rivet = ({ position }) => {
    const positions = {
      topLeft: { top: '8px', left: '8px' },
      topRight: { top: '8px', right: '8px' },
      bottomLeft: { bottom: '8px', left: '8px' },
      bottomRight: { bottom: '8px', right: '8px' },
    };
    
    return (
      <div 
        className="absolute w-3 h-3 rounded-full"
        style={{ 
          ...positions[position],
          background: `radial-gradient(circle at 30% 30%, ${adjustColor(PALETTE.ink, 60)}, ${PALETTE.ink}, ${adjustColor(PALETTE.ink, -20)})`,
          boxShadow: `inset 1px 1px 0px rgba(255,255,255,0.3), inset -1px -1px 0px rgba(0,0,0,0.5)`,
        }} 
      />
    );
  };

  return (
    <div 
      className={`relative bg-cream ${className}`}
      style={{
        border: `${DESIGN_TOKENS.borders.thick} solid ${PALETTE.ink}`,
        boxShadow: DESIGN_TOKENS.shadows.panel,
        borderRadius: DESIGN_TOKENS.borderRadius.xl,
      }}
    >
      {showRivets && (
        <>
          <Rivet position="topLeft" />
          <Rivet position="topRight" />
          <Rivet position="bottomLeft" />
          <Rivet position="bottomRight" />
        </>
      )}
      
      {title && (
        <div 
          className="text-center py-3 mx-8 mb-2"
          style={{
            fontFamily: DESIGN_TOKENS.fonts.display,
            fontSize: DESIGN_TOKENS.fontSizes.displaySm,
            color: PALETTE.ink,
            borderBottom: `2px solid ${PALETTE.ink}`,
            letterSpacing: '2px',
            textShadow: `2px 2px 0px ${adjustColor(PALETTE.ink, 60)}`,
          }}
        >
          {title}
        </div>
      )}
      
      <div className="p-6">
        {children}
      </div>
    </div>
  );
}

/**
 * RetroLogo - Two-tone gradient pixel wordmark per reference
 * (lighter top half / darker bottom half, chunky ink outline + hard shadow)
 */
export function RetroLogo({
  topText = 'RADIO',
  bottomText = 'BASEBALL',
  size = 'lg',
  className = '',
}) {
  const sizes = {
    sm: { top: '20px', bottom: '24px' },
    md: { top: '26px', bottom: '32px' },
    lg: { top: '32px', bottom: '40px' },
    xl: { top: '40px', bottom: '50px' },
  };

  // Hard-stop two-tone fill (SNES banding, no smooth blend)
  const twoTone = (light, dark) =>
    `linear-gradient(180deg, ${light} 0%, ${light} 48%, ${dark} 48%, ${dark} 100%)`;

  const LogoLine = ({ text, fontSize, light, dark }) => {
    const baseText = {
      fontFamily: DESIGN_TOKENS.fonts.display,
      fontSize,
      letterSpacing: '4px',
      fontWeight: 'bold',
      lineHeight: 1.2,
      whiteSpace: 'nowrap',
    };
    return (
      <div className="relative inline-block">
        {/* Hard drop shadow layer */}
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            ...baseText,
            color: PALETTE.ink,
            transform: 'translate(4px, 4px)',
          }}
        >
          {text}
        </div>
        {/* Gradient fill layer with ink outline */}
        <div
          style={{
            ...baseText,
            position: 'relative',
            backgroundImage: twoTone(light, dark),
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            WebkitTextStroke: `1.5px ${PALETTE.ink}`,
          }}
        >
          {text}
        </div>
      </div>
    );
  };

  return (
    <div className={`text-center ${className}`}>
      <div>
        <LogoLine text={topText} fontSize={sizes[size].top} light="#B5691F" dark="#7A4210" />
      </div>
      <div>
        <LogoLine text={bottomText} fontSize={sizes[size].bottom} light="#6BAF55" dark="#3A6E2D" />
      </div>
    </div>
  );
}

/**
 * RetroInputField - Styled input with label
 */
export function RetroInputField({
  label,
  type = 'text',
  placeholder,
  value,
  onChange,
  required = false,
  className = '',
  inputClassName = '',
  ...props
}) {
  return (
    <div className={className}>
      {label && (
        <label 
          className="block mb-2"
          style={{
            fontFamily: DESIGN_TOKENS.fonts.ui,
            fontWeight: 600,
            fontSize: DESIGN_TOKENS.fontSizes.uiSm,
            color: PALETTE.ink,
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
          }}
        >
          {label}
        </label>
      )}
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
        className={`w-full ${inputClassName}`}
        style={{
          fontFamily: DESIGN_TOKENS.fonts.ui,
          fontSize: DESIGN_TOKENS.fontSizes.uiMd,
          padding: '14px 16px',
          background: '#FFFFFF',
          color: PALETTE.ink,
          border: `${DESIGN_TOKENS.borders.medium} solid ${PALETTE.ink}`,
          boxShadow: DESIGN_TOKENS.shadows.inset,
          outline: 'none',
          borderRadius: DESIGN_TOKENS.borderRadius.md,
        }}
        {...props}
      />
    </div>
  );
}

/**
 * RetroLink - Styled link text
 */
export function RetroLink({
  children,
  href,
  onClick,
  color = 'grass',
  className = '',
}) {
  const colors = {
    grass: PALETTE.grass,
    dirt: PALETTE.dirt,
    ink: PALETTE.ink,
    cream: PALETTE.cream,
  };

  const linkStyle = {
    fontFamily: DESIGN_TOKENS.fonts.ui,
    fontWeight: 600,
    fontSize: DESIGN_TOKENS.fontSizes.uiSm,
    color: colors[color],
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  };

  if (href) {
    return (
      <a 
        href={href} 
        className={`hover:opacity-80 hover:underline ${className}`}
        style={linkStyle}
      >
        {children}
      </a>
    );
  }

  return (
    <button
      onClick={onClick}
      className={`hover:opacity-80 hover:underline ${className}`}
      style={linkStyle}
    >
      {children}
    </button>
  );
}

/**
 * RetroText - Styled text with consistent typography
 */
export function RetroText({
  children,
  size = 'md',
  color = 'ink',
  weight = 'normal',
  italic = false,
  display = false, // Set true for display font
  className = '',
}) {
  const sizes = {
    xs: DESIGN_TOKENS.fontSizes.uiXs,
    sm: DESIGN_TOKENS.fontSizes.uiSm,
    md: DESIGN_TOKENS.fontSizes.uiMd,
    lg: DESIGN_TOKENS.fontSizes.uiLg,
    xl: DESIGN_TOKENS.fontSizes.uiXl,
  };

  const colors = {
    ink: PALETTE.ink,
    cream: PALETTE.cream,
    grass: PALETTE.grass,
    dirt: PALETTE.dirt,
    muted: `${PALETTE.ink}99`,
    mutedLight: `${PALETTE.cream}99`,
  };

  return (
    <span 
      className={className}
      style={{
        fontFamily: display ? DESIGN_TOKENS.fonts.display : DESIGN_TOKENS.fonts.ui,
        fontSize: sizes[size],
        color: colors[color],
        fontStyle: italic ? 'italic' : 'normal',
        fontWeight: weight === 'bold' ? 700 : weight === 'semibold' ? 600 : 400,
      }}
    >
      {children}
    </span>
  );
}

/**
 * RetroPageTitle - Centered display-font page title with heavy shadow
 * Use color='cream' on dark stripe backgrounds, 'ink' on light backgrounds
 */
export function RetroPageTitle({
  children,
  color = 'cream',
  size = 'md',
  className = '',
}) {
  const sizes = {
    sm: DESIGN_TOKENS.fontSizes.displaySm,
    md: DESIGN_TOKENS.fontSizes.displayMd,
    lg: DESIGN_TOKENS.fontSizes.displayLg,
  };
  const isCream = color === 'cream';
  return (
    <h1
      className={`text-center ${className}`}
      style={{
        fontFamily: DESIGN_TOKENS.fonts.display,
        fontSize: sizes[size],
        color: isCream ? PALETTE.cream : PALETTE.ink,
        letterSpacing: '2px',
        textShadow: isCream
          ? `2px 2px 0px ${PALETTE.ink}, 4px 4px 0px rgba(0,0,0,0.45)`
          : `2px 2px 0px ${PALETTE.cream}, 3px 3px 0px rgba(0,0,0,0.35)`,
      }}
    >
      {children}
    </h1>
  );
}

/**
 * RetroDivider - Decorative divider line
 */
export function RetroDivider({
  color = 'ink',
  className = '',
}) {
  const colors = {
    ink: PALETTE.ink,
    grass: PALETTE.grass,
    dirt: PALETTE.dirt,
  };

  return (
    <div 
      className={`w-full h-1 ${className}`}
      style={{
        background: `linear-gradient(90deg, transparent 0%, ${colors[color]} 20%, ${colors[color]} 80%, transparent 100%)`,
      }}
    />
  );
}

// Utility: Adjust color brightness
function adjustColor(hex, amount) {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + amount));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0xFF) + amount));
  const b = Math.min(255, Math.max(0, (num & 0xFF) + amount));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}
